// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadProductCategories from "@conklin/App/Handlers/XNG/ProductCategories";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import ListItem from "@conklin/MobiusOverrides/Lists/ListItem";
import UnorderedList from "@conklin/MobiusOverrides/Lists/UnorderedList";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { UnorderedListProps } from "@insite/mobius/Lists";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    parentCategories = "parentCategories",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.parentCategories]: string[];
        [fields.title]: string;
    };
}

export interface ProductCategoriesStyles {
    wrapper?: InjectableCss;
    carouselWrapper?: InjectableCss;
    cardContainer?: InjectableCss;
    centeringWrapper?: InjectableCss;
    cardContent?: InjectableCss;
    cardImageLink?: LinkPresentationProps;
    cardTitle?: LinkPresentationProps;
    cardSubLinksList?: UnorderedListProps;
    cardSubLink?: LinkPresentationProps;
    viewAllLink?: LinkPresentationProps;
    title?: TypographyPresentationProps;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const viewKey = getDataViewKey({ categories: props.fields?.parentCategories, expand: ["subCategories"] });
    const categoriesState = (state as CustomAppState).xProductCategories.views?.[viewKey];
    return {
        parentCategories: categoriesState?.categories,
        isLoading: categoriesState?.loading,
    };
};

const mapDispatchToProps = {
    loadProductCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const productCategoriesStyles: ProductCategoriesStyles = {};

const styles = productCategoriesStyles;
const ProductCategories: FC<Props> = ({ fields, parentCategories, isLoading, loadProductCategories }) => {
    useEffect(() => {
        if (!isLoading && !parentCategories && fields.parentCategories && fields.parentCategories.length !== 0) {
            loadProductCategories({
                categories: fields.parentCategories,
                expand: ["subCategories"],
            });
        }
    }, [fields.parentCategories]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="product-categories-widget">
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner size={24} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (!parentCategories && !isLoading) {
        return null;
    }

    return (
        <StyledWrapper data-test-selector="product-categories-widget">
            {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
            <StyledWrapper {...styles.wrapper}>
                {parentCategories.map(category => {
                    const subCategories = category.subCategories ? [...category.subCategories] : [];
                    return (
                        <StyledWrapper {...styles.cardContainer} key={category.id}>
                            <Link
                                {...styles.cardImageLink}
                                href={category.path}
                                aria-label={category.imageAltText || category.shortDescription || "Category Image"}
                            >
                                <LazyImage
                                    src={category.smallImagePath}
                                    altText={category.imageAltText || category.shortDescription || "Category Image"}
                                    width="88px"
                                    height="88px"
                                />
                            </Link>
                            <StyledWrapper {...styles.cardContent}>
                                <Link
                                    {...styles.cardTitle}
                                    href={category.path}
                                    aria-label={category.shortDescription || "Category Title"}
                                >
                                    {category.shortDescription}
                                </Link>
                                {subCategories
                                    .sort((a, b) => a.sortOrder - b.sortOrder)
                                    .slice(0, 3)
                                    .map(subCat => (
                                        <UnorderedList {...styles.cardSubLinksList} key={subCat.id}>
                                            <ListItem>
                                                <Link
                                                    {...styles.cardSubLink}
                                                    href={subCat.path}
                                                    aria-label={subCat.name || "Subcategory"}
                                                >
                                                    {subCat.name}
                                                </Link>
                                            </ListItem>
                                        </UnorderedList>
                                    ))}
                                <Link
                                    {...styles.viewAllLink}
                                    href={category.path}
                                    aria-label={`View all in ${category.shortDescription || "this category"}`}
                                >
                                    {translate("View All")}
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductCategories),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "link",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "Explore Our Product Categories",
            },
            {
                name: fields.parentCategories,
                editorTemplate: "CategoriesField",
                displayName: "Heading Category",
                defaultValue: [],
                fieldType: "General",
                validate: (value, item) => {
                    if (value.length > 50) {
                        return "50 Categories are allowed to select!";
                    }
                    return "";
                },
            },
        ],
    },
};

export default widgetModule;
